import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useEffect } from "react";
import { RecentTransactionsResponse } from "../lusca-search-api/generated-src";
import { RecentTransactionsApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";
import {
  RecentTransactionsSearchParams,
  TransactionsInfo,
} from "../types/common";

type RecentTransactionsResponseWithLogGroups = RecentTransactionsResponse & {
  logGroups?: string[];
};

function fetchRecentTransactions(
  api: ReturnType<typeof RecentTransactionsApiFactory>,
  params: RecentTransactionsSearchParams
) {
  return api
    .getRecentTransactions(
      params.stage,
      params.countries,
      params.gl,
      params.usecases,
      params.paymentMethod,
      params.paymentType,
      params.paymentMethodClass,
      params.asin,
      params.endTime,
      params.deep,
      params.features
    )
    .then(
      (res: AxiosResponse<RecentTransactionsResponseWithLogGroups>) => res.data
    );
}

export default function useFetchRecentTransactions(
  params: RecentTransactionsSearchParams
): TransactionsInfo {
  const api = RecentTransactionsApiFactory();
  const qc = useQueryClient();
  const key = ["recentTransactions", JSON.stringify(params)];

  const { data, error, isLoading, isFetching } = useQuery<
    RecentTransactionsResponseWithLogGroups,
    Error
  >(key, () => fetchRecentTransactions(api, params), {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    enabled: Boolean(params.stage),
    staleTime: Infinity,
  });

  // Prefetch next page to reduce wait times
  useEffect(() => {
    const ts = data?.nextPageTimestamp;
    if (!ts) return;

    const nextParams = { ...params, endTime: ts };
    const nextKey = ["recentTransactions", JSON.stringify(nextParams)];
    if (!qc.getQueryData(nextKey)) {
      qc.prefetchQuery(
        nextKey,
        () => fetchRecentTransactions(api, nextParams),
        { staleTime: Infinity }
      ).catch(() => {});
    }
  }, [data?.nextPageTimestamp, params, qc, api]);

  return {
    transactions: data?.transactions ?? [],
    executedQuery: data?.executedQuery,
    logGroups: data?.logGroups,
    nextPageTimestamp: data?.nextPageTimestamp,
    isLoading: isLoading || isFetching,
    error: error ?? null,
  };
}
